import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Skeleton } from '@/shared/ui/Skeleton';
import { formatDate } from '@/shared/utils/format';
import { referralsApi } from '@/modules/referrals/api/referralsApi';
import { useToast } from '@/shared/ui/Toast';
import { practiceFeatureFlagKeys } from '@/shared/hooks/usePracticeFeatureFlags';
import { agencyApi } from '../../api/agencyApi';
import { agencyKeys } from '../../queryKeys';
import adminStyles from '../admin/AdminCards.module.css';

function apiErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
  return message ?? (err instanceof Error ? err.message : fallback);
}

/**
 * Modules › Patient referrals: the feature switch (the same audited flag endpoint every other flag
 * uses) and what turning it on does. Off for every practice by default.
 */
export function ReferralsModulePanel({ practiceId, flags }: { practiceId: string; flags: Record<string, boolean> }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const on = flags.patientReferrals === true;
  const flagMutation = useMutation({
    mutationFn: (next: boolean) => agencyApi.updatePracticeFlags(practiceId, { patientReferrals: next }),
    onSuccess: (_r, next) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      // FeatureRoute and the sidebar read this cache; without it the section stays hidden for a minute.
      queryClient.invalidateQueries({ queryKey: practiceFeatureFlagKeys.flags(practiceId) });
      toast.success(next ? 'Patient referrals switched on for this practice.' : 'Patient referrals switched off. Nothing was deleted.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not update the flag')),
  });

  const offices = useQuery({
    queryKey: ['agency', 'referral-offices', practiceId] as const,
    queryFn: () => referralsApi.agencyClaimedOffices(practiceId),
    enabled: on,
    staleTime: 60_000,
  });

  return (
    <>
      <Card title="Permissions (practice-wide)">
        <div className={adminStyles.aiActions}>
          <div className={adminStyles.aiActionRow}>
            <div>
              <div className={adminStyles.aiActionLabel}>Patient referrals</div>
              <div className={adminStyles.aiActionDesc}>
                Shows Operations › Referrals: a referral slip with the tooth chart (dental) or body-region picker
                (medical), suggested nearby specialists, X-ray and document uploads, a secure emailed link for the
                receiving office (on PracticeNova or not), a text to the patient from the practice's own texting
                number, status tracking, reminders and reports back. Off by default. The practice sees it after
                their next page load.
              </div>
            </div>
            <Button variant={on ? 'secondary' : 'primary'} size="sm" loading={flagMutation.isPending} onClick={() => flagMutation.mutate(!on)}>
              {on ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </div>
      </Card>
      {on && (
        <Card
          title="Offices reached (leads)"
          subtitle="Outside offices that received a referral from this practice and verified a contact at the link. Reachable, warm, not yet on PracticeNova."
        >
          {offices.isLoading ? (
            <Skeleton height={80} />
          ) : !offices.data?.length ? (
            <div className={adminStyles.aiActionDesc}>No outside office has opened a referral from this practice yet.</div>
          ) : (
            <div className={adminStyles.aiActions}>
              {offices.data
                .filter((o) => !o.onPracticeNova)
                .map((o) => (
                  <div key={o.id} className={adminStyles.aiActionRow}>
                    <div>
                      <div className={adminStyles.aiActionLabel}>
                        {o.name}
                        {o.specialty ? ` · ${o.specialty.replace(/_/g, ' ')}` : ''}
                      </div>
                      <div className={adminStyles.aiActionDesc}>
                        {[o.email, o.phone, [o.city, o.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                        <br />
                        {o.referralsReceived} referral{o.referralsReceived === 1 ? '' : 's'} received, {o.referralsScheduled} scheduled
                        {o.claimedAt ? ` · first verified ${formatDate(o.claimedAt)}` : ''}
                        {o.lastSeenAt ? ` · last seen ${formatDate(o.lastSeenAt)}` : ''}
                        {o.contacts.some((c) => c.leadDetailsAt) ? ' · confirmed their details' : ''}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Card>
      )}
      <Card title="What to check before switching on">
        <div className={adminStyles.aiActions}>
          <div className={adminStyles.aiActionRow}>
            <div>
              <div className={adminStyles.aiActionLabel}>Template</div>
              <div className={adminStyles.aiActionDesc}>
                The slip follows the practice's vertical and specialty (Settings tab). A generalist sees suggested
                specialists first; a specialist sees generalists and peers.
              </div>
            </div>
          </div>
          <div className={adminStyles.aiActionRow}>
            <div>
              <div className={adminStyles.aiActionLabel}>Patient texts</div>
              <div className={adminStyles.aiActionDesc}>
                "Text patient a copy" works only once the location has a live texting number (Settings › Texting);
                otherwise the toggle is off and the slip says why. Receiving offices are always emailed.
              </div>
            </div>
          </div>
          <div className={adminStyles.aiActionRow}>
            <div>
              <div className={adminStyles.aiActionLabel}>Files and AI</div>
              <div className={adminStyles.aiActionDesc}>
                Uploads need object storage (R2) configured; the comment draft and triage summary need the Anthropic
                key. Each degrades to a plain slip when missing.
              </div>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}
